import { AsyncJobStartResponse } from '../types/asyncJob';

import { apiClient } from './apiClient';
import { startAndPollJob } from './jobPollingService';

const ADO_BASE = '/api/ado-integration';

export interface AdoConnectionStatus {
  connected: boolean;
  ado_user_email?: string | null;
  expires_at?: string | null;
  scopes?: string | null;
  connected_at?: string | null;
}

export interface AdoOrganization {
  account_id: string;
  account_name: string;
  organization_url: string;
}

export interface AdoProject {
  id: string;
  name: string;
  description?: string | null;
  url?: string | null;
  state?: string | null;
}

export type AdoWorkItemType =
  | 'Epic'
  | 'Feature'
  | 'User Story'
  | 'Product Backlog Item'
  | 'Requirement'
  | 'Issue'
  | 'Task'
  | 'Bug';

export type AdoProcessTemplate = 'Scrum' | 'Agile' | 'Basic' | 'CMMI';

export interface GeneratedWorkItem {
  temp_id: string;
  /**
   * Free-form work-item type string. Codie returns one of the
   * process-template-specific names (e.g. ``Product Backlog Item``
   * for Scrum or ``Requirement`` for CMMI), so we keep this open.
   */
  type: string;
  title: string;
  description?: string | null;
  acceptance_criteria?: string | null;
  tags: string[];
  estimated_effort?: number | null;
  children: GeneratedWorkItem[];
}

export interface GenerateBacklogResponse {
  project_id: number;
  generated_at: string;
  root_items: GeneratedWorkItem[];
  process_template: AdoProcessTemplate;
  model_deployment?: string | null;
  source_markdown?: string | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
}

export interface SendToCodieRequest {
  title: string;
  summary?: string | null;
  root_items: GeneratedWorkItem[];
  analysis_model?: string | null;
  analysis_tokens_in?: number | null;
  analysis_tokens_out?: number | null;
}

export interface ProjectAdoMapping {
  project_id: number;
  ado_org: string;
  ado_project: string;
  ado_project_id?: string | null;
  set_by: string;
  created_at: string;
  updated_at: string;
}

export interface SetProjectAdoMappingRequest {
  ado_org: string;
  ado_project: string;
  ado_project_id?: string | null;
  confirm_change?: boolean;
}

export interface ArchitectureImportSummary {
  id: number;
  project_id: number;
  codie_import_id: string;
  ado_org: string;
  ado_project: string;
  title: string;
  summary?: string | null;
  work_item_count: number;
  status: string;
  correlation_id: string;
  analysis_model?: string | null;
  imported_batch_id?: string | null;
  submitted_at: string;
  status_checked_at?: string | null;
  imported_at?: string | null;
  created_by: string;
}

interface OAuthStartResponse {
  authorization_url: string;
  state: string;
}

interface ProjectMarkdownPreview {
  project_id: number;
  markdown: string;
}

export interface BacklogEventCounts {
  total: number;
  added: number;
  updated: number;
  deleted: number;
}

export interface BacklogEvent {
  id: string;
  adoOrg: string;
  adoProject: string;
  eventKind: string;
  workItemId?: number | null;
  workItemType?: string | null;
  state?: string | null;
  stateFrom?: string | null;
  stateTo?: string | null;
  summary?: string | null;
  changedBy?: string | null;
  tags?: string[] | null;
  archieOrigin?: boolean | null;
  receivedAt: string;
  [key: string]: unknown;
}

export interface BacklogEventsResponse {
  items: BacklogEvent[];
  counts: BacklogEventCounts;
  addedIds: number[];
  updatedIds: number[];
  deletedIds: number[];
  nextCursor: string | null;
  cursorAdvanced: boolean;
  monitoringEnabled: boolean;
}

export interface BacklogDigest {
  id: string;
  adoOrg: string;
  adoProject: string;
  windowStart: string;
  windowEnd: string;
  significant: boolean;
  headline?: string | null;
  reasons?: string[] | null;
  counts?: BacklogEventCounts | null;
  highlights?: unknown[] | null;
  consumedAt?: string | null;
  [key: string]: unknown;
}

export interface BacklogDigestsResponse {
  items: BacklogDigest[];
}

export interface BacklogCursor {
  project_id: number;
  ado_org: string;
  ado_project: string;
  last_seen_at: string | null;
  updated_at: string;
}

export interface BacklogEventsQuery {
  since?: string | null;
  excludeArchie?: boolean;
  excludeCodie?: boolean;
  limit?: number;
  eventKinds?: string[];
  /** When false, the read does not advance the server-side cursor. */
  advanceCursor?: boolean;
}

export interface BacklogDigestsQuery {
  since?: string | null;
  significantOnly?: boolean;
  unconsumedOnly?: boolean;
  limit?: number;
}

/**
 * Client for the Archie ↔ Codie / Azure DevOps integration backend.
 *
 * Codie's plugin is responsible for materialising work items in ADO;
 * Archie publishes the analysed backlog and tracks the resulting
 * import status. All calls go through `apiClient` so they inherit
 * Archie's Entra-ID bearer token.
 */
export class AdoIntegrationService {
  /** Get the current user's ADO SSO connection status. */
  public async getConnectionStatus(): Promise<AdoConnectionStatus> {
    return apiClient.get<AdoConnectionStatus>(`${ADO_BASE}/connection`);
  }

  /** Build the Microsoft authorization URL for the user to consent. */
  public async startConnection(
    redirectUri: string,
  ): Promise<OAuthStartResponse> {
    return apiClient.post<OAuthStartResponse>(
      `${ADO_BASE}/connection/start`,
      { redirect_uri: redirectUri },
    );
  }

  /** Exchange the authorization code for ADO tokens. */
  public async exchangeConnection(
    code: string,
    state: string,
    redirectUri: string,
  ): Promise<AdoConnectionStatus> {
    return apiClient.post<AdoConnectionStatus>(
      `${ADO_BASE}/connection/exchange`,
      { code, state, redirect_uri: redirectUri },
    );
  }

  /** Disconnect the current user from Azure DevOps. */
  public async disconnect(): Promise<void> {
    await apiClient.delete<void>(`${ADO_BASE}/connection`);
  }

  /** List ADO organizations the user can access. */
  public async listOrganizations(): Promise<AdoOrganization[]> {
    return apiClient.get<AdoOrganization[]>(`${ADO_BASE}/organizations`);
  }

  /** List projects in the named ADO organization. */
  public async listProjects(organizationName: string): Promise<AdoProject[]> {
    const safe = encodeURIComponent(organizationName);
    return apiClient.get<AdoProject[]>(
      `${ADO_BASE}/organizations/${safe}/projects`,
    );
  }

  /** Preview the markdown that will be sent to codie-api. */
  public async getProjectMarkdown(projectId: number): Promise<string> {
    const res = await apiClient.get<ProjectMarkdownPreview>(
      `${ADO_BASE}/projects/${projectId}/markdown`,
    );
    return res.markdown;
  }

  /** Ask codie-api to derive a backlog tree from the project context. */
  public async generateBacklog(
    projectId: number,
    options: {
      additionalInstructions?: string;
      sourceMarkdownOverride?: string;
    } = {},
  ): Promise<GenerateBacklogResponse> {
    return startAndPollJob<GenerateBacklogResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `${ADO_BASE}/projects/${projectId}/generate-backlog/async`,
          {
            additional_instructions: options.additionalInstructions,
            source_markdown_override: options.sourceMarkdownOverride,
          },
        ),
    );
  }

  /** Publish the backlog to Codie's architecture-import endpoint. */
  public async sendToCodie(
    projectId: number,
    payload: SendToCodieRequest,
  ): Promise<ArchitectureImportSummary> {
    return startAndPollJob<ArchitectureImportSummary>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `${ADO_BASE}/projects/${projectId}/send-to-codie/async`,
          payload,
        ),
    );
  }

  /** Architecture imports submitted for a single project. */
  public async listProjectImports(
    projectId: number,
    limit = 50,
  ): Promise<ArchitectureImportSummary[]> {
    return apiClient.get<ArchitectureImportSummary[]>(
      `${ADO_BASE}/projects/${projectId}/imports?limit=${limit}`,
    );
  }

  /** All architecture imports authored by the current user. */
  public async listMyImports(
    limit = 100,
  ): Promise<ArchitectureImportSummary[]> {
    return apiClient.get<ArchitectureImportSummary[]>(
      `${ADO_BASE}/imports?limit=${limit}`,
    );
  }

  /**
   * Pull the latest status for a single import from Codie (AR-3).
   *
   * Used by the per-row Refresh button on the imports table to
   * detect `Add to Backlog` (→ `imported`) and `Ignore / Archive`
   * (→ `rejected`) clicks made inside the Codie ADO plugin.
   */
  public async refreshImportStatus(
    importId: number,
  ): Promise<ArchitectureImportSummary> {
    return apiClient.post<ArchitectureImportSummary>(
      `${ADO_BASE}/imports/${importId}/refresh-status`,
      {},
    );
  }

  /**
   * Return the persistent ADO target for a project, or `null` when none
   * has been set yet. The backend returns 404 in the unset case; we
   * translate that into a `null` for caller convenience.
   */
  public async getProjectAdoMapping(
    projectId: number,
  ): Promise<ProjectAdoMapping | null> {
    try {
      return await apiClient.get<ProjectAdoMapping>(
        `${ADO_BASE}/projects/${projectId}/ado-mapping`,
      );
    } catch (err) {
      const status = (err as { status?: number; statusCode?: number })
        .status ?? (err as { statusCode?: number }).statusCode;
      if (status === 404) {
        return null;
      }
      throw err;
    }
  }

  /** Create or change the persistent ADO target for a project. */
  public async setProjectAdoMapping(
    projectId: number,
    payload: SetProjectAdoMappingRequest,
  ): Promise<ProjectAdoMapping> {
    return apiClient.put<ProjectAdoMapping>(
      `${ADO_BASE}/projects/${projectId}/ado-mapping`,
      payload,
    );
  }

  /** Remove the ADO target for a project (owner-only). */
  public async deleteProjectAdoMapping(projectId: number): Promise<void> {
    await apiClient.delete<void>(
      `${ADO_BASE}/projects/${projectId}/ado-mapping`,
    );
  }

  /**
   * List recent backlog events for the project's mapped ADO target.
   *
   * Calls Codie's ``GET /api/azdo/backlog/events`` via Archie. When
   * ``since`` is omitted the backend uses the persisted per-project
   * cursor and advances it on success.
   */
  public async listBacklogEvents(
    projectId: number,
    opts: BacklogEventsQuery = {},
  ): Promise<BacklogEventsResponse> {
    const params = new URLSearchParams();
    if (opts.since) {
      params.set('since', opts.since);
    }
    if (opts.excludeArchie !== undefined) {
      params.set('excludeArchie', opts.excludeArchie ? 'true' : 'false');
    }
    if (opts.excludeCodie !== undefined) {
      params.set('excludeCodie', opts.excludeCodie ? 'true' : 'false');
    }
    if (opts.limit !== undefined) {
      params.set('limit', String(opts.limit));
    }
    if (opts.advanceCursor !== undefined) {
      params.set('advanceCursor', opts.advanceCursor ? 'true' : 'false');
    }
    if (opts.eventKinds && opts.eventKinds.length > 0) {
      params.set('eventKinds', opts.eventKinds.join(','));
    }
    const qs = params.toString();
    const suffix = qs ? `?${qs}` : '';
    return apiClient.get<BacklogEventsResponse>(
      `${ADO_BASE}/projects/${projectId}/backlog/events${suffix}`,
    );
  }

  /** List Codie's recent daily backlog digests for the project. */
  public async listBacklogDigests(
    projectId: number,
    opts: BacklogDigestsQuery = {},
  ): Promise<BacklogDigestsResponse> {
    const params = new URLSearchParams();
    if (opts.since) {
      params.set('since', opts.since);
    }
    if (opts.significantOnly) {
      params.set('significantOnly', 'true');
    }
    if (opts.unconsumedOnly) {
      params.set('unconsumedOnly', 'true');
    }
    if (opts.limit !== undefined) {
      params.set('limit', String(opts.limit));
    }
    const qs = params.toString();
    const suffix = qs ? `?${qs}` : '';
    return apiClient.get<BacklogDigestsResponse>(
      `${ADO_BASE}/projects/${projectId}/backlog/digests${suffix}`,
    );
  }

  /** Mark a Codie digest as consumed (idempotent). */
  public async ackBacklogDigest(
    projectId: number,
    digestId: string,
  ): Promise<void> {
    const safe = encodeURIComponent(digestId);
    await apiClient.post<void>(
      `${ADO_BASE}/projects/${projectId}/backlog/digests/${safe}/ack`,
      {},
    );
  }

  /** Return the persisted polling cursor, or ``null`` when unset. */
  public async getBacklogCursor(
    projectId: number,
  ): Promise<BacklogCursor | null> {
    return apiClient.get<BacklogCursor | null>(
      `${ADO_BASE}/projects/${projectId}/backlog/cursor`,
    );
  }

  /** Drop the persisted cursor so the next poll re-scans 24h. */
  public async resetBacklogCursor(projectId: number): Promise<void> {
    await apiClient.delete<void>(
      `${ADO_BASE}/projects/${projectId}/backlog/cursor`,
    );
  }

  /**
   * Advance the per-project cursor to now ("mark all as seen").
   *
   * The explicit, user-driven mutation that backs the panel's
   * "Mark all as seen" action so the read view can stay idempotent.
   */
  public async markBacklogSeen(
    projectId: number,
  ): Promise<BacklogCursor> {
    return apiClient.post<BacklogCursor>(
      `${ADO_BASE}/projects/${projectId}/backlog/mark-seen`,
      {},
    );
  }
}

export const adoIntegrationService = new AdoIntegrationService();
